/**
 * 动作播放器（P2a）：纯函数状态机，不碰 DOM、不看时钟——给它“过了多少毫秒”，它告诉你现在播到哪一帧、发生了哪些事件。
 * 同样的输入序列在任何帧率下结果一样（每次步进按帧的 duration_ms 累加，不按渲染帧数），便于游戏用同一时刻判定。
 *
 * 规矩（对应合同稿第 4、5 节）：
 * - 要去别的状态（request）：从当前站稳的状态按过渡段找最短路一段段播。
 * - 过渡段只在首帧、尾帧切：还停在首帧且首帧是可切换帧时，新指令可以当场取消这段（身体还在起点）；动起来以后先排队，播完再改道。
 * - 循环段在循环尾、或它声明的可切换帧让位给新目标；一次性段（起止相同）也认它声明的帧（F0 修订 C1）。
 * - 缺帧（markBroken）：这段以后不再播，画面落到这段的 fallback_state 的稳定姿势，再按路线继续。
 * - 找不到路：直接落到目标状态的稳定姿势（世界状态以世界事实为准，动作只是表现），发 fallback 事件，不停在半路。
 * - 每次步进的时长有上限（MAX_STEP_MS）：页面在后台停了很久回来，不会一口气快进过好几段动作。
 * - 减少动态效果（settle）：不播过渡，直接落到目标状态的稳定姿势。
 * 游戏用（F0 第 4 节，P3a）：
 * - face：同一状态有好几个朝向的循环段（move_up……）时按朝向挑；正在走的循环换向时接着同一帧号播（保相位），不从头来；
 * - cut：规则事件那一帧当场切进一次性段或过渡段（eat、blocked、hit、end_win……），不等可切换帧；
 *   带上规则事件的标识（核心步进号＋同一步里的次序，F0 修订 R2 第 2 条）时按它去重：同一个事件、比它早的事件都不再切；
 *   段里的帧标记只是表现（音效、粒子），不算分、不扣命（R2 第 1 条）；
 * - 过渡段播完落到一个没有循环段的状态（END）时停在这段的末帧（hold），不换成推出来的稳定姿势。
 * - 挂起（F0 修订 B7）：规则事件段的起点既不是身体站着的状态、也不是正在过渡去的状态（比如还在空中就进了球），先挂起、只留最新一个，
 *   站到它的起点状态再播；当场能切的新事件不清掉挂起的段（空中进球 → 落地播完进 WAIT → 再播得意）。
 *   过渡途中来了从它终点出发的段（蓄力没蹲完就出手）：当它到站、当场切，规则不等动画。
 * - 等可切换帧（修订 C2）：调用方要求时，一次性段播到它声明的可切换帧才切（篮球 react 段里来了回球、终局）；贪吃不要求，照旧当场切。
 */
import type { MotionClip, MotionFrame, MotionStateId, PreparedMotion } from "./manifest";

/** 单次步进最多算这么多毫秒（约 6 帧 60Hz）；再长当作卡顿，不补播 */
export const MAX_STEP_MS = 100;

export interface MotionPlayer {
  motion: PreparedMotion;
  /** 最近一次站稳的状态 */
  state: MotionStateId;
  /** 要去的状态 */
  target: MotionStateId;
  /** 正在播的段；null＝停在 state 的稳定姿势 */
  clip: MotionClip | null;
  frame: number;
  /** 当前帧已经停了多久（毫秒） */
  elapsed: number;
  /** 缺帧、不再播的段（clip_key） */
  broken: ReadonlySet<string>;
  /** 同一状态有几个朝向的循环段时挑哪个（up、down、left、right……）；null＝取清单里第一个 */
  facing: string | null;
  /** 停在稳定姿势时画哪一帧：刚播完的过渡段末帧；null＝画推出来的稳定姿势 */
  hold: MotionFrame | null;
  /** 最近一次切段用过的规则事件标识；更早或相同的事件再来不重播 */
  lastRuleEvent: RuleEventId | null;
  /** 挂起的规则事件段（F0 修订 B7、C2）：起点状态还没到、或要等可切换帧；只留最新一个 */
  pending: PendingCut | null;
}

/** 挂起的一段：站到它的起点状态（要等可切换帧的，还要正好在一次性段的可切换帧上）就播 */
export interface PendingCut {
  clip_key: string;
  atInterrupt: boolean;
}

export interface CutOptions {
  /**
   * 正在播一次性段时，等到它声明的可切换帧再切（篮球 react_score、react_miss 里来了回球、终局：F0 修订 C2）。
   * 不给就当场切（贪吃的 eat、blocked、hit：修订 R2 第 3 条）。用不用由游戏的适配层按事件定，播放器不认识游戏。
   */
  atInterrupt?: boolean;
}

/** 规则事件的唯一标识：局号（没有按 0 算）、核心的步进号、同一步里的次序（F0 修订 R2 第 2 条；B 的核心按 "round:step:seq" 给） */
export interface RuleEventId {
  round?: number;
  step: number;
  seq: number;
}

const notAfter = (a: RuleEventId, b: RuleEventId) => {
  const ra = a.round ?? 0;
  const rb = b.round ?? 0;
  if (ra !== rb) return ra < rb;
  return a.step < b.step || (a.step === b.step && a.seq <= b.seq);
};

export type MotionEvent =
  | { type: "frame"; clip_key: string; frame: number; event: string }
  | { type: "arrived"; state: MotionStateId }
  /** missing_frame：段缺帧；no_path：找不到路，直接落到目标；no_target_pose：目标画不出来，不落过去（F0 修订 G3），state 是没去成的目标 */
  | { type: "fallback"; reason: "missing_frame" | "no_path" | "no_target_pose"; clip_key: string | null; state: MotionStateId };

function loopOf(motion: PreparedMotion, state: MotionStateId, broken: ReadonlySet<string>, facing: string | null): MotionClip | null {
  const loops = motion.clips.filter((clip) => clip.loop && clip.from_state === state && !broken.has(clip.clip_key));
  return loops.find((clip) => clip.facing === facing) ?? loops[0] ?? null;
}

/** 从 from 到 to 的最短过渡路线（段序列）；没有路为 null，from===to 为空数组。同样短的取清单里靠前的，结果稳定。 */
export function routeOf(motion: PreparedMotion, from: MotionStateId, to: MotionStateId, broken: ReadonlySet<string> = new Set()): MotionClip[] | null {
  if (from === to) return [];
  const edges = motion.clips.filter((clip) => !clip.loop && !broken.has(clip.clip_key));
  const previous = new Map<MotionStateId, MotionClip>();
  const seen = new Set<MotionStateId>([from]);
  const queue: MotionStateId[] = [from];
  while (queue.length) {
    const at = queue.shift()!;
    for (const clip of edges) {
      if (clip.from_state !== at || seen.has(clip.to_state)) continue;
      seen.add(clip.to_state);
      previous.set(clip.to_state, clip);
      if (clip.to_state === to) {
        const route: MotionClip[] = [];
        for (let node: MotionStateId = to; node !== from; node = previous.get(node)!.from_state) route.unshift(previous.get(node)!);
        return route;
      }
      queue.push(clip.to_state);
    }
  }
  return null;
}

/** 正在播过渡段：身体在两个状态之间 */
const inTransit = (p: MotionPlayer) => !!p.clip && !p.clip.loop && p.clip.from_state !== p.clip.to_state;
/** 身体站在、或正要到的状态：过渡途中算它的终点 */
const arriving = (p: MotionPlayer) => (inTransit(p) ? p.clip!.to_state : p.state);
/** 正在播一次性段、还没到它声明的可切换帧 */
const beforeInterrupt = (p: MotionPlayer) => !!p.clip && !p.clip.loop && p.clip.from_state === p.clip.to_state && !p.clip.interrupt_frames.includes(p.frame);
/**
 * 规则事件段此刻能不能开播：从身体站在（或正要到）的状态出发；要等可切换帧的，正播着的一次性段得在可切换帧上。
 * 过渡途中来了从它终点出发的段（蓄力还没蹲完就出手、滞空姿势还没停稳就落地）：规则不等动画，当它到站、当场切（修订 R2 第 3 条）；
 * 不这样的话，快按快放会让「出手」挂起、又被随后的「落地」顶掉，画面卡在蓄力里。
 */
const canStart = (p: MotionPlayer, clip: MotionClip, atInterrupt: boolean) => clip.from_state === arriving(p) && !(atInterrupt && beforeInterrupt(p));
/** 出不去的状态（END）：没有任何段从它出发 */
const stuckIn = (motion: PreparedMotion, state: MotionStateId) => !motion.clips.some((clip) => clip.from_state === state);

function frameEvents(clip: MotionClip, frame: number, events: MotionEvent[]) {
  for (const mark of clip.events) if (mark.frame === frame) events.push({ type: "frame", clip_key: clip.clip_key, frame, event: mark.name });
}

function begin(player: MotionPlayer, clip: MotionClip | null, events: MotionEvent[]): MotionPlayer {
  if (clip) frameEvents(clip, 0, events);
  return { ...player, clip, frame: 0, elapsed: 0, hold: clip ? null : player.hold };
}

/**
 * 挂起的段此刻能播就开播（清掉挂起）；还不能播、或这段已经缺帧，返回 null（缺帧的段不播，挂起留着也不会再开播）。
 * 挂起的段要等身体站稳在它的起点（B7「落地播完进 WAIT 再播」）：过渡途中不放，哪怕正往它的起点去。
 */
function flushPending(p: MotionPlayer, events: MotionEvent[]): MotionPlayer | null {
  const pending = p.pending;
  if (!pending || inTransit(p)) return null;
  const clip = p.motion.clips.find((c) => c.clip_key === pending.clip_key);
  if (!clip || p.broken.has(clip.clip_key) || !canStart(p, clip, pending.atInterrupt)) return null;
  return begin({ ...p, target: clip.to_state, pending: null }, clip, events);
}

/** 站稳在 state 上以后：有挂起的规则事件段先播它；到了目标就待机（有循环播循环，没有就停在稳定姿势）；没到就按路线开始下一段。 */
function settleAt(player: MotionPlayer, state: MotionStateId, events: MotionEvent[]): MotionPlayer {
  const here: MotionPlayer = { ...player, state, clip: null, frame: 0, elapsed: 0 };
  const pended = flushPending(here, events);
  if (pended) return pended;
  if (state === here.target) return begin(here, loopOf(here.motion, state, here.broken, here.facing), events);
  const route = routeOf(here.motion, state, here.target, here.broken);
  if (!route || route.length === 0) {
    // 找不到路（能走的段都缺帧或清单没给）：直接落到目标的稳定姿势，不停在半路
    events.push({ type: "fallback", reason: "no_path", clip_key: null, state: here.target });
    events.push({ type: "arrived", state: here.target });
    return begin({ ...here, state: here.target, hold: null }, loopOf(here.motion, here.target, here.broken, here.facing), events);
  }
  return begin(here, route[0], events);
}

export function createPlayer(motion: PreparedMotion, start?: MotionStateId, facing: string | null = null): MotionPlayer {
  const state = start !== undefined && motion.stable.has(start) ? start : motion.referenceState;
  const player: MotionPlayer = { motion, state, target: state, clip: null, frame: 0, elapsed: 0, broken: new Set(), facing, hold: null, lastRuleEvent: null, pending: null };
  return begin(player, loopOf(motion, state, player.broken, facing), []);
}

/**
 * 要去 target。清单里没有这个状态的稳定姿势（没给、或它的段不合格被去掉）就不去：原样接着播，发 fallback{no_target_pose}（F0 修订 G3）。
 * 停在稳定姿势时立刻出发；过渡段还在首帧且首帧可切换时当场改道；别的情况等到可切换帧或段尾再改道（step 里处理）。
 */
export function request(player: MotionPlayer, target: MotionStateId): { player: MotionPlayer; events: MotionEvent[] } {
  if (target === player.target) return { player, events: [] };
  if (!player.motion.stable.has(target)) return { player, events: [{ type: "fallback", reason: "no_target_pose", clip_key: null, state: target }] };
  const next = { ...player, target };
  const events: MotionEvent[] = [];
  const clip = next.clip;
  if (!clip) return { player: settleAt(next, next.state, events), events };
  if (!clip.loop && next.frame === 0 && clip.interrupt_frames.includes(0)) {
    // 还没动：新路线第一段还是这段就接着播，不重来；否则当场取消、从起点改道
    const route = routeOf(next.motion, clip.from_state, target, next.broken);
    if (route?.[0] !== clip) return { player: settleAt(next, clip.from_state, events), events };
  }
  return { player: next, events };
}

/**
 * 往前推 dtMs 毫秒（负数、非数当 0，超过 MAX_STEP_MS 截到上限）。
 * 进入带事件标记的帧时发 frame 事件；站稳一个状态时发 arrived。
 */
export function step(player: MotionPlayer, dtMs: number): { player: MotionPlayer; events: MotionEvent[] } {
  let dt = Math.min(Math.max(0, Number.isFinite(dtMs) ? dtMs : 0), MAX_STEP_MS);
  let p = player;
  const events: MotionEvent[] = [];
  while (dt > 0 && p.clip) {
    const clip = p.clip;
    const remaining = clip.frames[p.frame].duration_ms - p.elapsed;
    if (dt < remaining) {
      p = { ...p, elapsed: p.elapsed + dt };
      break;
    }
    dt -= remaining;
    const nextFrame = p.frame + 1;
    if (nextFrame >= clip.frames.length) {
      if (!clip.loop) {
        // 过渡段播完：站稳在终点
        events.push({ type: "arrived", state: clip.to_state });
        p = settleAt({ ...p, hold: clip.frames[clip.frames.length - 1] }, clip.to_state, events);
      } else if (p.target === p.state) {
        p = begin(p, clip, events);
      } else {
        // 循环尾：让位
        p = settleAt(p, p.state, events);
      }
      continue;
    }
    p = { ...p, frame: nextFrame, elapsed: 0 };
    frameEvents(clip, nextFrame, events);
    // 一次性段播到可切换帧：等在这里的规则事件段（修订 C2）先播
    const pended = flushPending(p, events);
    if (pended) {
      p = pended;
      continue;
    }
    if ((clip.loop || clip.from_state === clip.to_state) && p.target !== p.state && clip.interrupt_frames.includes(nextFrame)) p = settleAt(p, p.state, events);
  }
  return { player: p, events };
}

/** 某段的帧加载不出来：这段以后不再播；正在播它的话，画面落到它的 fallback_state（循环段就停在本状态），再按路线继续。 */
export function markBroken(player: MotionPlayer, clipKey: string): { player: MotionPlayer; events: MotionEvent[] } {
  if (player.broken.has(clipKey)) return { player, events: [] };
  const p: MotionPlayer = { ...player, broken: new Set(player.broken).add(clipKey) };
  const events: MotionEvent[] = [];
  const clip = p.clip;
  if (clip?.clip_key !== clipKey) return { player: p, events };
  const landing = clip.loop ? p.state : clip.fallback_state;
  events.push({ type: "fallback", reason: "missing_frame", clip_key: clipKey, state: landing });
  if (!clip.loop) events.push({ type: "arrived", state: landing });
  return { player: settleAt({ ...p, hold: null }, landing, events), events };
}

/**
 * 换朝向（游戏每帧按宠物朝向调）。正在播本状态的循环段、而新朝向另有循环段时，换过去并接着同一帧号播（保相位）；
 * 正在播一次性段或过渡段时只记下朝向，播完落回循环时按它挑。不发事件。
 */
export function face(player: MotionPlayer, facing: string): MotionPlayer {
  if (player.facing === facing) return player;
  const p = { ...player, facing };
  const clip = p.clip;
  if (!clip || !clip.loop) return p;
  // 这个朝向没有循环段（或缺帧）：照旧走原来那段，只记朝向
  const next = p.motion.clips.find((c) => c.loop && c.from_state === p.state && c.facing === facing && !p.broken.has(c.clip_key));
  if (!next) return p;
  const frame = Math.min(p.frame, next.frames.length - 1);
  return { ...p, clip: next, frame, elapsed: Math.min(p.elapsed, next.frames[frame].duration_ms) };
}

/**
 * 规则事件那一帧当场切段（游戏用）：从身体站着（或正在过渡去）的状态出发的一次性段或过渡段，不等可切换帧；
 * 过渡途中被接走时当它到站（发 arrived）。同一段正在播就从头再播（连吃、再撞）。清单里没有、缺帧、是循环段：不理（照旧播）。
 * 起点对不上：先挂起，只留最新一个，站到起点再播（F0 修订 B7）；
 * 调用方要求等可切换帧（options.atInterrupt）而正播着的一次性段还没到可切换帧：也挂起，到了那一帧再切（修订 C2）。
 * 给了规则事件标识：这个事件或更早的事件已经处理过就不理（重渲染、切回前台、重复投递都不重播）；
 * 处理过的标识照记，哪怕这一段没切成（清单里还没有这段），免得同一事件以后再切。
 * 终局段（进一个再也出不去的状态，比如 END）一旦开播、或已经站在终局状态上，就不再被切走也不挂起，之后的规则事件只更新结算页（F0 修订 B6）。
 */
export function cut(player: MotionPlayer, clipKey: string, ruleEvent?: RuleEventId, options: CutOptions = {}): { player: MotionPlayer; events: MotionEvent[] } {
  if (ruleEvent && player.lastRuleEvent && notAfter(ruleEvent, player.lastRuleEvent)) return { player, events: [] };
  const seen = ruleEvent ? { ...player, lastRuleEvent: ruleEvent } : player;
  const at = arriving(seen);
  if (stuckIn(seen.motion, at)) return { player: seen, events: [] };
  const clip = seen.motion.clips.find((c) => c.clip_key === clipKey);
  if (!clip || clip.loop || seen.broken.has(clipKey)) return { player: seen, events: [] };
  const atInterrupt = options.atInterrupt === true;
  if (!canStart(seen, clip, atInterrupt)) return { player: { ...seen, pending: { clip_key: clipKey, atInterrupt } }, events: [] };
  const events: MotionEvent[] = [];
  // 过渡段没播完就被规则事件接走：身体当它到站了
  if (inTransit(seen)) events.push({ type: "arrived", state: at });
  return { player: begin({ ...seen, state: at, target: clip.to_state }, clip, events), events };
}

/** 减少动态效果：不播过渡，直接落到目标状态的稳定姿势（也不播循环）。本来就站在目标上（包括在播循环）不发 arrived。 */
export function settle(player: MotionPlayer): { player: MotionPlayer; events: MotionEvent[] } {
  const already = player.state === player.target && (!player.clip || player.clip.loop);
  const events: MotionEvent[] = already ? [] : [{ type: "arrived", state: player.target }];
  return { player: { ...player, state: player.target, clip: null, frame: 0, elapsed: 0, hold: null }, events };
}

/**
 * 不走过渡、直接站到 state（F0 修订 G3 的「退」）：有待机循环就从头播它，没有就停在稳定姿势；挂起的段一并清掉。
 * 换了状态（或正在过渡途中）发 arrived；state 画不出来就原样交回，不落过去。
 */
export function standAt(player: MotionPlayer, state: MotionStateId): { player: MotionPlayer; events: MotionEvent[] } {
  if (!player.motion.stable.has(state)) return { player, events: [] };
  const events: MotionEvent[] = player.state === state && !inTransit(player) ? [] : [{ type: "arrived", state }];
  const here: MotionPlayer = { ...player, state, target: state, clip: null, frame: 0, elapsed: 0, hold: null, pending: null };
  return { player: begin(here, loopOf(here.motion, state, here.broken, here.facing), events), events };
}

/** 此刻该画的那一帧：段里的当前帧，或者站稳状态的稳定姿势。 */
export function currentFrame(player: MotionPlayer): MotionFrame {
  if (player.clip) return player.clip.frames[Math.min(player.frame, player.clip.frames.length - 1)];
  return player.hold ?? player.motion.stable.get(player.state)!;
}
